import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CardConfiguration } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { ReplaySubject, Subject, combineLatest, interval, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { FamilyLinkDetails, Link } from '../../../../global/global.model';
import {
    BardCodeType,
    CardActionPayload,
    CardConfigDataFE,
    CardSelectorFE,
    CardStatus,
    CardType,
    FEWallet,
    GetUserActiveSubscriptionsResponse,
    UserCard,
    UserCardFE,
    UserWallet,
    WalletDictionnary,
    UserRecord,
    CardActions,
} from '../../../user.model';
import { AddFamilyMemberSelectionDialogComponent } from 'src/app/user/dialogs/add-family-member-selection-dialog/add-family-member-selection-dialog.component';
import { CreateCardDialogComponent } from 'src/app/user/dialogs/create-card-dialog/create-card-dialog.component';
import { IncompleteUserRecordComponent } from 'src/app/user/dialogs/incomplete-user-record-dialog/incomplete-user-record.component';
import { LinkCardDialogComponent } from 'src/app/user/dialogs/link-card-dialog/link-card-dialog.component';
import { UpdateCardDialogComponent } from 'src/app/user/dialogs/update-card-dialog/update-card-dialog.component';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { organisationSelector, languageSelector, organisationConfigurationSelector, organisationFeaturesConfigurationSelector } from 'src/app/global/store/global.selectors';
import { userRecordSelector } from 'src/app/user/store/user.selectors';
import { CreateNotConnectedCardDialogComponent } from 'src/app/user/dialogs/create-not-connected-card-dialog/create-not-connected-card-dialog.component';
import { RefreshUserSubscriptionsAction } from 'src/app/user/store/user.actions';

// Default max members is unlimited
const MAX_FAMILY_MEMBERS = 0;

@Component({
    selector: 'app-user-card',
    templateUrl: './user-cards.component.html',
    styleUrls: ['./user-cards.component.scss'],
    animations: [
        trigger('selectedCardChange', [transition(':enter', [style({ opacity: 0 }), animate('100ms', style({ opacity: 1 }))]), transition(':leave', [animate('10ms', style({ opacity: 0 }))])]),
    ],
})
export class UserCardComponent implements OnInit, OnDestroy {
    public destroy$ = new Subject();
    public updateFamily$ = new ReplaySubject();
    public updateWallet$ = new ReplaySubject();
    private cancelPolling$ = new Subject<void>();
    private updatingCardType: CardType | null = null;
    private cardUpdatePollStarted = false;

    public loading = false;

    // For use in template
    public CardStatus = CardStatus;
    public CardType = CardType;
    public BardCodeType = BardCodeType;

    public cardConfig: CardConfiguration[] = [];
    public selectableCards: CardSelectorFE[] = [];
    public family: FamilyLinkDetails[] = [];
    public familyLinks: Array<Link> = [];
    public pendingFamilyLinks: FamilyLinkDetails[] = [];
    public inviteFamilyLinks: FamilyLinkDetails[] = [];

    public activeWallet: FEWallet;
    public isConnected = true;

    public qrcodeOpt = {
        elementType: NgxQrcodeElementTypes.URL,
        correctionLevel: NgxQrcodeErrorCorrectionLevels.HIGH,
    };

    private _selectedCardType: CardType;
    get selectedCardType() {
        return this._selectedCardType;
    }
    set selectedCardType(cardType: CardType) {
        this._selectedCardType = cardType;
        this.scrollToSelectedCard();
    }
    get activeCard() {
        return this.feWallet[this.selectedCardType];
    }

    private _selectedProfile: string;
    get selectedProfile() {
        return this._selectedProfile;
    }
    set selectedProfile(id: string) {
        this._selectedProfile = id;
    }

    public familyCardActive = false;

    get totalFamilyMembers(): number {
        return 1 + this.family.length + this.pendingFamilyLinks.length + this.inviteFamilyLinks.length;
    }

    private platformMaxFamilyMembers: number;

    get maxFamilyMembers(): number {
        return this.platformMaxFamilyMembers ?? MAX_FAMILY_MEMBERS;
    }

    @ViewChild('activeCard') activeCardVC: ElementRef;

    private defaultRequiredFieldsForCard = ['picture', 'birthDate', 'firstName', 'lastName', 'secEmail', 'sex'];

    constructor(
        private router: Router,
        private translateService: TranslateService,
        private userService: UserService,
        private dialog: MatDialog,
        private cdRef: ChangeDetectorRef,
        private store: Store<State>,
    ) {}

    feWallet: WalletDictionnary = {};
    userRecord: UserRecord;
    cardConfigData: CardConfigDataFE;
    userFamily: Link[];

    ngOnInit() {
        this.store
            .select(languageSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((lang) => this.translateService.use(lang)),
            )
            .subscribe();

        combineLatest([this.store.select(organisationSelector), this.store.select(organisationConfigurationSelector)])
            .pipe(
                takeUntil(this.destroy$),
                filter(([org, config]) => !!org && !!config),
                map(([org, config]) => {
                    this.cardConfigData = { org, config };
                    this.cardConfig = config.support.cards;
                }),
                tap(() => this.updateWallet$.next()),
            )
            .subscribe();

        this.updateWallet$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => (this.loading = true)),
                switchMap(() =>
                    this.store.select(userRecordSelector).pipe(
                        takeUntil(this.destroy$),
                        filter((userRecord) => !!userRecord),
                    ),
                ),
                tap((userRecord) => (this.userRecord = userRecord)),
                switchMap((userRecord) =>
                    combineLatest([
                        this.userService.getUserActiveSubscriptions(userRecord.id).pipe(catchError(() => of([]))),
                        this.userService.getUserFamilyLinks(userRecord.id).pipe(catchError(() => of([]))),
                        this.store.select(organisationConfigurationSelector),
                        this.userService.getWallet(userRecord.id).pipe(
                            catchError(() => of({ cards: [] })),
                            map((response) => response),
                        ),
                    ]).pipe(
                        takeUntil(this.destroy$),
                        map(([uSubs, family, config, wallet]) => {
                            this.platformMaxFamilyMembers = config?.family?.maxFamilyMember;

                            this.selectableCards = this.buildCardSelector(config.support.cards, wallet);
                            this.feWallet = this.buildWallet(wallet, uSubs, config.support.cards);

                            if (this.updatingCardType) {
                                const realCard = wallet.cards?.find((c) => c.cardType === this.updatingCardType);
                                if (this.cardUpdatePollStarted && realCard?.status === CardStatus.ACTIVE) {
                                    this.updatingCardType = null;
                                    this.cardUpdatePollStarted = false;
                                    this.cancelPolling$.next();
                                } else {
                                    if (this.feWallet[this.updatingCardType]) {
                                        this.feWallet[this.updatingCardType].status = CardStatus.PROCESSING;
                                    }
                                    const selector = this.selectableCards.find((c) => c.cardType === this.updatingCardType);
                                    if (selector) {
                                        selector.status = CardStatus.PROCESSING;
                                    }
                                }
                            }

                            if (!this.selectedCardType) {
                                if (config.support.cards.find((card) => card.type === CardType.LEGACY)) {
                                    this.selectedCardType = CardType.LEGACY;
                                } else {
                                    this.selectedCardType =
                                        config.support.cards
                                            .map((c) => {
                                                return c.type;
                                            })
                                            .indexOf(CardType.BCITI) > -1
                                            ? CardType.BCITI
                                            : CardType.CITIZEN;
                                }
                            }

                            this.userFamily = family;
                            this.selectedProfile = this.userRecord.id;
                            this.family = [];
                            this.familyLinks = [];
                            family.forEach((member) => {
                                this.familyLinks.push(member);
                                if (member.cityApproved && member.familyApproved) {
                                    this.family.push(member.userId);
                                    // userId property actually contains profile data and IS NOT just an id string
                                }
                                this.populatePendingFamilyLinks();
                                this.populateInviteFamilyLinks();
                            });

                            this.loading = false;
                        }),
                    ),
                ),
            )
            .subscribe();

        this.translateService.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.updateWallet$.next();
                }),
            )
            .subscribe();

        this.cdRef.detectChanges();
    }

    buildCardSelector(cardConfig: CardConfiguration[], wallet: UserWallet): CardSelectorFE[] {
        const cardSelector = [];
        cardConfig.forEach((card) => {
            const cardLabel = card.identity?.label[this.translateService.currentLang];
            if (card.requiredCards) {
                const allow: boolean[] = [];
                card.requiredCards.forEach((reqCard) => {
                    wallet.cards.forEach((walletCard) => {
                        if (reqCard === walletCard.cardType && walletCard.status === CardStatus.ACTIVE) {
                            allow.push(true);
                        }
                    });
                });
                if (allow.length === card.requiredCards.length) {
                    let selectorToPush;
                    wallet.cards.forEach((walletCard) => {
                        if (card.type === walletCard.cardType) {
                            selectorToPush = { label: cardLabel, cardType: card.type, status: walletCard.status, renewal: walletCard.renewal };
                        }
                    });
                    cardSelector.push(selectorToPush || { label: cardLabel, cardType: card.type, status: CardStatus.INACTIVE });
                }
            } else {
                let selectorToPush;
                wallet.cards.forEach((walletCard) => {
                    if (card.type === walletCard.cardType) {
                        selectorToPush = { label: cardLabel, cardType: card.type, status: walletCard.status, renewal: walletCard.renewal };
                    }
                });
                cardSelector.push(selectorToPush || { label: cardLabel, cardType: card.type, status: CardStatus.INACTIVE });
            }
        });
        return cardSelector;
    }

    buildWallet(wallet: UserWallet, subscriptions?: GetUserActiveSubscriptionsResponse[], config?: CardConfiguration[]): FEWallet {
        const buildingWallet = {};
        this.cardConfig.forEach((card) => (buildingWallet[card.type] = { cardType: card.type, subscriptions: [] }));
        wallet?.cards?.forEach((card) => (buildingWallet[card.cardType] = { ...buildingWallet[card.cardType], ...card }));
        subscriptions?.forEach((sub) => {
            if (sub.status === 'ACTIVE') buildingWallet[sub.cardType].subscriptions.push(sub);
        });
        config?.forEach((card) => (buildingWallet[card.type].linkable = card.linkable));
        return buildingWallet;
    }

    openCreateCardDialog(card: Partial<UserCardFE>, action: CardActions, userRecord: UserRecord) {
        this.store
            .select(organisationFeaturesConfigurationSelector)
            .pipe(
                take(1),
                map((features) => {
                    if (this.validateUserRecordForCardCreation(userRecord, features.cardRequiredFields || this.defaultRequiredFieldsForCard || [])) {
                        if (this.isConnected == false && card.cardType == 'LIBRARY') {
                            this.dialog
                                .open(CreateNotConnectedCardDialogComponent, {
                                    panelClass: 'create-dialog',
                                    data: { card, action, userRecord },
                                })
                                .afterClosed()
                                .subscribe(() => this.updateWallet$.next());
                        } else {
                            this.dialog
                                .open(CreateCardDialogComponent, {
                                    panelClass: 'create-dialog',
                                    width: '560px',
                                    data: { card, action, userRecord },
                                })
                                .afterClosed()
                                .subscribe(() => this.updateWallet$.next());
                        }
                    } else {
                        this.dialog.open(IncompleteUserRecordComponent, {
                            maxWidth: '600px',
                            data: { userRecord, requiredFields: features.cardRequiredFields || this.defaultRequiredFieldsForCard },
                        });
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Populate the pendingFamilyLink list so it's easier to know which links are pending. - PENDING APPROVALS
     */
    private populatePendingFamilyLinks() {
        this.pendingFamilyLinks = [];
        this.familyLinks?.forEach((link) => {
            if (!link.deleted && !link.cityApproved && !link.isSource) {
                this.pendingFamilyLinks.push(link.userId);
            }
        });
    }

    /**
     * Populate the pendingFamilyLink list so it's easier to know which links are pending. - PENDING APPROVALS
     */
    private populateInviteFamilyLinks() {
        this.inviteFamilyLinks = [];
        this.familyLinks?.forEach((link) => {
            if (!link.deleted && !link.familyApproved && link.isSource) {
                // Si le lien de famille n'est pas effacé et qu'il n'est pas approuvé par la ville
                // et que le liens n'est pas family approved et que le lien est la source de la demande
                // alors nous avons une action a prendre en tan t qu'usager (Accepter ou Refuser la demande).
                this.inviteFamilyLinks.push(link.userId);
            }
        });
    }

    validateUserRecordForCardCreation(userRecord: UserRecord, fields: string[]) {
        // null/undefined check
        if (!fields?.length) {
            return true;
        }

        // check user record against config fields
        let valid = true;
        fields.forEach((field) => {
            if (!userRecord[field]) valid = false;
        });
        return valid;
    }

    handleCardActions(event: CardActionPayload) {
        this.isConnected = this.cardConfig.find((c) => c.type === event.card.cardType)?.isConnected;
        let currentUserRecord = this.userRecord;
        this.store
            .select(organisationFeaturesConfigurationSelector)
            .pipe(
                take(1),
                map((features) => {
                    switch (event.action) {
                        case CardActions.SUBSCRIBE:
                        case CardActions.RENEW:
                            this.openCreateCardDialog(event.card, event.action, currentUserRecord);
                            break;
                        case CardActions.LINK:
                            if (this.isConnected == false && event.card.cardType == 'LIBRARY') {
                                this.openCreateCardDialog(event.card, event.action, currentUserRecord);
                            } else {
                                this.handleCardLink(event.card.cardType, currentUserRecord);
                            }
                            break;
                        case CardActions.UPDATEEXPDATE:
                            this.openUpdateExpDateCardDialog(event.card, event.action, currentUserRecord, this.selectableCards);
                            break;
                        default:
                            console.warn('unknown cardAction', event);
                            break;
                    }
                }),
            )
            .subscribe();
    }

    openUpdateExpDateCardDialog(card: Partial<UserCardFE>, action: CardActions, userRecord: UserRecord, selectableCards: CardSelectorFE[]) {
        this.dialog
            .open(UpdateCardDialogComponent, {
                disableClose: true,
                panelClass: 'create-dialog',
                width: '560px',
                data: { card, action, userRecord, selectableCards },
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                this.updateWallet$.next();
                // Delay dispatch to allow backend to finish processing the card update
                setTimeout(() => {
                    this.store.dispatch(RefreshUserSubscriptionsAction());
                }, 1000);

                if (result?.connectedCardUpdated) {
                    this.pollForCardUpdate(card.cardType);
                }
            });
    }

    private pollForCardUpdate(cardType: CardType) {
        this.cancelPolling$.next();
        this.updatingCardType = cardType;
        this.cardUpdatePollStarted = false;

        const POLL_INTERVAL_MS = 10000;
        const POLL_COUNT = 3;

        interval(POLL_INTERVAL_MS)
            .pipe(take(POLL_COUNT), takeUntil(this.destroy$), takeUntil(this.cancelPolling$))
            .subscribe({
                next: () => {
                    this.cardUpdatePollStarted = true;
                    this.updateWallet$.next();
                },
                complete: () => {
                    if (this.updatingCardType) {
                        this.updatingCardType = null;
                        this.cardUpdatePollStarted = false;
                        this.updateWallet$.next();
                    }
                },
            });
    }

    handleCardLink(cardType: CardType, userRecord: UserRecord) {
        this.store
            .select(organisationFeaturesConfigurationSelector)
            .pipe(
                take(1),
                map((features) => {
                    if (this.validateUserRecordForCardCreation(userRecord, features.cardRequiredFields || this.defaultRequiredFieldsForCard || [])) {
                        this.dialog
                            .open(LinkCardDialogComponent, {
                                panelClass: 'create-dialog',
                                data: { cardType, userRecord },
                            })
                            .afterClosed()
                            .subscribe((result: boolean) => {
                                if (result) this.updateWallet$.next();
                            });
                    } else {
                        this.dialog.open(IncompleteUserRecordComponent, {
                            maxWidth: '600px',
                            data: { profile: userRecord, requiredFields: features.cardRequiredFields || this.defaultRequiredFieldsForCard },
                        });
                    }
                }),
            )
            .subscribe();
    }

    getCardButtonIcon(cardType: CardType) {
        const cardConfig = this.cardConfig?.find((c) => c.type === cardType);
        switch (cardType) {
            case CardType.BCITI:
            case CardType.CITIZEN:
            case CardType.LEGACY:
            case CardType.LIBRARY:
            case CardType.SPORTS:
                return cardConfig.identity?.icon;
            default:
                return 'icon-my-card';
        }
    }

    scrollToSelectedCard() {
        this.activeCardVC?.nativeElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
        });
    }

    onLinkFamiliyMember() {
        this.dialog.open(AddFamilyMemberSelectionDialogComponent, {
            panelClass: 'bciti-dialog',
            data: {
                refreshFamilyLinks: () => this.refreshFamilyLinks(),
                userRecord: this.userRecord,
            },
        });
    }

    public selectFamilyMember(userId: string) {
        this.loading = true;
        this.selectedProfile = userId;
        this.familyCardActive = true;
        this.cardConfig.forEach((card) => {
            if (card.type === CardType.BCITI) this.selectedCardType = CardType.BCITI;
            if (card.type === CardType.LEGACY) this.selectedCardType = CardType.LEGACY;
            if (card.type === CardType.CITIZEN) this.selectedCardType = CardType.CITIZEN;
        });

        combineLatest([this.userService.getWallet(userId), this.userService.getUserActiveSubscriptions(userId)])
            .pipe(take(1))
            .subscribe({
                next: ([wallet, subscriptions]) => {
                    this.selectableCards = this.buildCardSelector(this.cardConfig, wallet);
                    this.activeWallet = this.buildWallet(wallet, subscriptions, this.cardConfig);
                    this.loading = false;
                    this.scrollToSelectedCard();
                },
                error: (error) => {
                    this.selectableCards = this.buildCardSelector(this.cardConfig, {
                        cards: [],
                    });
                    this.activeWallet = this.buildWallet(null, null, this.cardConfig);
                    // TODO: Refactor when api correctly returns a 200 for an empty wallet or empty subscriptions instead of a 404
                    // this.contextService.errorMessage(this.translate.instant('pages.user-cards.family.error.wallet'));
                    this.loading = false;
                },
            });
    }

    public selectSelf() {
        this.selectedProfile = this.userRecord.id;
        this.familyCardActive = false;
        this.updateWallet$.next();
    }

    public refreshFamilyLinks() {
        this.updateWallet$.next();
    }

    onMyFamilyEdit() {
        const familyTab = document.getElementById('tab_admin-user-family');
        if (familyTab) familyTab.click();
    }

    profileIsMissingPrimaryCard(profile: any): boolean {
        const primaryCard: UserCard = profile?.wallets?.[0]?.cards?.find(
            (card: UserCard) => card?.cardType === CardType.BCITI || card?.cardType === CardType.CITIZEN || card?.cardType === CardType.LEGACY,
        );
        return !primaryCard || primaryCard.status === CardStatus.PROCESSING;
    }

    ngOnDestroy() {
        this.cancelPolling$.next();
        this.cancelPolling$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
